import { classNames, twMerge } from '@panwds/tailwindcss';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { FocusOn } from 'react-focus-on';
import { noop } from '../../utils/noop.js';
import { useShouldRender } from '../../utils/useShouldRender.js';
import { Card } from '../Card/index.js';
import { Portal } from '../Portal/index.js';
import { CloseConfirmation } from './CloseConfirmation.js';
const sizes = {
    sm: {
        maxHeight: '48vh',
        width: '400px',
        widthStatic: 400,
        minHeight: '200px'
    },
    md: {
        maxHeight: '72vh',
        width: '640px',
        widthStatic: 640,
        minHeight: '320px'
    },
    lg: {
        maxHeight: '84vh',
        width: '800px',
        widthStatic: 800,
        minHeight: '400px'
    },
    xl: {
        maxHeight: '92vh',
        width: '1024px',
        widthStatic: 1024,
        minHeight: '512px'
    },
    full: { maxHeight: '92vh', height: '92vh', width: '92vw' }
};
const widthOffset = 35;
const maxHeightOffset = '5.625rem';
const bodyMeasurements = {
    sm: {
        maxHeight: `calc(${sizes.sm.maxHeight} - ${maxHeightOffset})`,
        width: `calc(${sizes.sm.width} - ${widthOffset})`,
        widthStatic: sizes.sm.widthStatic - widthOffset
    },
    md: {
        maxHeight: `calc(${sizes.md.maxHeight} - ${maxHeightOffset})`,
        width: `calc(${sizes.md.width} - ${widthOffset})`,
        widthStatic: sizes.md.widthStatic - widthOffset
    },
    lg: {
        maxHeight: `calc(${sizes.lg.maxHeight} - ${maxHeightOffset})`,
        width: `calc(${sizes.lg.width} - ${widthOffset})`,
        widthStatic: sizes.lg.widthStatic - widthOffset
    },
    xl: {
        maxHeight: `calc(${sizes.xl.maxHeight} - ${maxHeightOffset})`,
        width: `calc(${sizes.xl.width} - ${widthOffset})`,
        widthStatic: sizes.xl.widthStatic - widthOffset
    },
    full: {
        maxHeight: `calc(${sizes.full.height} - ${maxHeightOffset})`,
        height: `calc(${sizes.full.height} - ${maxHeightOffset})`,
        width: `calc(${sizes.full.width} - ${widthOffset})`
    }
};
const defaultEventsToClose = {
    enableClickOutside: false,
    enableEscapeKey: false
};
const invariantViolation = () => {
    throw new Error('Attempted to call useModalContext outside of Modal.');
};
export const ModalContext = createContext({
    closeModal: invariantViolation,
    closeModalWithConfirm: invariantViolation,
    size: 'md'
});
export const useModalContext = () => useContext(ModalContext);
/**
 * Renders its children in a focused mode in front of a backdrop.
 *
 * When the Modal is open, all other actions on the page are disabled and focus is trapped within the Modal.
 *
 * Set the initial focus within in the modal with one of two attributes:
 *
 *  - `data-autofocus` - the specific element will get focus on mount
 *  - `data-autofocus-inside` - the first interactive child will get focus on mount
 *
 *
 * ### Import Guide
 *
 * ```jsx
 * import {
 * useModal,
 * Modal,
 * ModalBody,
 * ModalFooter,
 * ModalHeader,
 * ModalCloseButton,
 * ConfirmationDialog
 * } from '@panwds/react-ui';
 * ```
 */
export const Modal = ({ children, confirmClose = false, confirmationDialog, dataMetrics, dataTestId, eventsToClose = defaultEventsToClose, onClose = noop, isOpen, size = 'md', shards, style = {}, addClassName }) => {
    const shouldRender = useShouldRender(isOpen);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const handleOnClose = useCallback(() => {
        confirmClose ? setShowConfirmation(true) : onClose();
    }, [confirmClose, setShowConfirmation, onClose]);
    if (!shouldRender && !isOpen) {
        return null;
    }
    const opacity0 = !shouldRender || !isOpen;
    const dialogClassName = twMerge('twds-fixed twds-bg-white dark:twds-shadow-dark-modal twds-top-0 twds-border-none dark:twds-border dark:twds-border-solid dark:twds-border-gray-950  twds-transform -twds-translate-x-1/2 twds-translate-y-10 twds-z-10 twds-max-w-full twds-transition-opacity twds-duration-300', opacity0 ? 'twds-opacity-0' : 'twds-opacity-100', addClassName);
    const maskClassName = classNames('twds-bg-steel-gray-400 dark:twds-bg-steel-gray-1000 twds-fixed twds-inset-0 twds-z-10 twds-transition-opacity twds-duration-200', opacity0 ? 'twds-opacity-0' : 'twds-opacity-70 dark:twds-opacity-80');
    const shouldHandleOnClose = { ...defaultEventsToClose, ...eventsToClose };
    return (React.createElement(Portal, { dataMetrics: dataMetrics, dataTestId: dataTestId || 'modal' },
        React.createElement("div", { className: maskClassName, style: { zIndex: 15 } }),
        React.createElement(FocusOn, { noIsolation: true, onClickOutside: shouldHandleOnClose['enableClickOutside'] ? handleOnClose : undefined, onEscapeKey: shouldHandleOnClose['enableEscapeKey'] ? handleOnClose : undefined, shards: shards },
            React.createElement(ModalContext.Provider, { value: {
                    closeModal: onClose,
                    closeModalWithConfirm: handleOnClose,
                    size
                } },
                React.createElement(Card, { addClassName: dialogClassName, "aria-modal": true, role: "dialog", style: {
                        ...sizes[size],
                        zIndex: 15,
                        ...style,
                        ...(style.minHeight && {
                            minHeight: `min(${typeof style.minHeight === 'number' ? `${style.minHeight}px` : style.minHeight}, ${sizes[size].maxHeight})`
                        }),
                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.24)',
                        ...{
                            '--tw-translate-x': '-50%',
                            '--tw-translate-y': '-50%',
                            left: '50%',
                            top: '50%'
                        }
                    } },
                    children,
                    confirmClose && showConfirmation && (React.createElement("div", { className: "twds-absolute twds-bg-white dark:twds-bg-gray-950 twds-h-full twds-top-0 twds-w-full twds-z-10 twds-rounded" },
                        React.createElement(CloseConfirmation, { onCancel: () => {
                                setShowConfirmation(false);
                            }, onDiscard: () => {
                                setShowConfirmation(false);
                                onClose();
                            } }))),
                    confirmationDialog && (React.createElement("div", { className: "twds-absolute twds-bg-white dark:twds-bg-gray-950 twds-h-full twds-top-0 twds-w-full twds-z-10 twds-rounded" }, confirmationDialog)))))));
};
Modal.bodyMeasurements = bodyMeasurements;
//# sourceMappingURL=Modal.js.map